import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";


const canadaUiText = (en, fr) =>
  (typeof document !== "undefined" && document.documentElement.lang === "fr") ? fr : en;

const UxContext = createContext(null);

export function useUx() {
  const value = useContext(UxContext);
  if (!value) throw new Error("useUx must be used inside UxProvider");
  return value;
}

export default function UxProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const [dialog, setDialog] = useState(null);
  const [promptValue, setPromptValue] = useState("");
  const [busy, setBusy] = useState(false);
  const idRef = useRef(0);

  const notify = useCallback((message, tone = "success", timeout = 3600) => {
    if (!message) return;
    const id = ++idRef.current;
    setToasts(current => [...current, { id, message, tone }]);
    window.setTimeout(() => setToasts(current => current.filter(item => item.id !== id)), timeout);
  }, []);

  const confirmAction = useCallback((options = {}) => {
    setPromptValue("");
    setDialog({ type: "confirm", ...options });
  }, []);

  const promptAction = useCallback((options = {}) => {
    setPromptValue(options.initialValue || "");
    setDialog({ type: "prompt", ...options });
  }, []);

  const closeDialog = useCallback(() => {
    if (busy) return;
    dialog?.externalResolve?.(dialog.type === "prompt" ? null : false);
    setDialog(null);
    setPromptValue("");
  }, [busy, dialog]);

  const acceptDialog = async () => {
    if (!dialog || busy) return;
    if (dialog.type === "prompt" && dialog.required !== false && !promptValue.trim()) return;
    setBusy(true);
    try {
      const value = dialog.type === "prompt" ? promptValue.trim() : true;
      const result = await dialog.onConfirm?.(dialog.type === "prompt" ? promptValue.trim() : undefined);
      if (result !== false) {
        dialog.externalResolve?.(value);
        setDialog(null);
        setPromptValue("");
      }
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    const onNotify = event => notify(event.detail?.message, event.detail?.tone || "success");
    const onConfirm = event => {
      const detail = event.detail || {};
      setPromptValue("");
      setDialog({
        type: "confirm",
        title: detail.title || canadaUiText("Confirm action", "Confirmer l’action"),
        message: detail.message || canadaUiText("Are you sure you want to continue?", "Voulez-vous vraiment continuer?"),
        confirmLabel: detail.confirmLabel || canadaUiText("Confirm", "Confirmer"),
        cancelLabel: detail.cancelLabel || canadaUiText("Cancel", "Annuler"),
        tone: detail.tone || "danger",
        externalResolve: detail.resolve,
      });
    };
    const onPrompt = event => {
      const detail = event.detail || {};
      setPromptValue(detail.initialValue || "");
      setDialog({
        type: "prompt",
        title: detail.title || canadaUiText("Additional information", "Renseignements supplémentaires"),
        message: detail.message || "",
        inputLabel: detail.inputLabel || canadaUiText("Details", "Détails"),
        placeholder: detail.placeholder || "",
        confirmLabel: detail.confirmLabel || canadaUiText("Save", "Enregistrer"),
        cancelLabel: detail.cancelLabel || canadaUiText("Cancel", "Annuler"),
        required: detail.required !== false,
        externalResolve: detail.resolve,
      });
    };
    window.addEventListener("cvi-notify", onNotify);
    window.addEventListener("cvi-confirm-request", onConfirm);
    window.addEventListener("cvi-prompt-request", onPrompt);
    return () => {
      window.removeEventListener("cvi-notify", onNotify);
      window.removeEventListener("cvi-confirm-request", onConfirm);
      window.removeEventListener("cvi-prompt-request", onPrompt);
    };
  }, [notify]);

  return (
    <UxContext.Provider value={{ notify, confirmAction, promptAction }}>
      {children}
      <div className="cvi-toast-stack" aria-live="polite" aria-atomic="false">
        {toasts.map(toast => (
          <div key={toast.id} className={`cvi-toast is-${toast.tone}`} role="status">
            <span className="cvi-toast-dot" aria-hidden="true" />
            <span>{toast.message}</span>
            <button type="button" aria-label="Dismiss" onClick={() => setToasts(current => current.filter(item => item.id !== toast.id))}>×</button>
          </div>
        ))}
      </div>
      {dialog && (
        <div className="cvi-dialog-backdrop" role="presentation" onMouseDown={event => { if (event.target === event.currentTarget) closeDialog(); }}>
          <div className="cvi-dialog" role="dialog" aria-modal="true" aria-labelledby="cvi-dialog-title">
            <div className="cvi-dialog-head">
              <div>
                <h3 id="cvi-dialog-title">{dialog.title || canadaUiText("Confirm action", "Confirmer l’action")}</h3>
                {dialog.message ? <p>{dialog.message}</p> : null}
              </div>
              <button type="button" className="cvi-dialog-close" onClick={closeDialog} aria-label={canadaUiText("Close", "Fermer")}>×</button>
            </div>
            {dialog.type === "prompt" ? (
              <div className="cvi-dialog-body">
                <label>{dialog.inputLabel || canadaUiText("Details", "Détails")}</label>
                <textarea autoFocus rows={4} value={promptValue} placeholder={dialog.placeholder || ""} onChange={event => setPromptValue(event.target.value)} />
              </div>
            ) : null}
            {dialog.details ? <div className="cvi-dialog-details">{dialog.details}</div> : null}
            <div className="cvi-dialog-actions">
              <button type="button" className="cvi-button-secondary" onClick={closeDialog} disabled={busy}>{dialog.cancelLabel || canadaUiText("Cancel", "Annuler")}</button>
              <button type="button" className={`cvi-button-primary${dialog.tone === "danger" ? " is-danger" : ""}`} onClick={acceptDialog} disabled={busy || (dialog.type === "prompt" && dialog.required !== false && !promptValue.trim())}>
                {busy ? "Working..." : (dialog.confirmLabel || canadaUiText("Confirm", "Confirmer"))}
              </button>
            </div>
          </div>
        </div>
      )}
    </UxContext.Provider>
  );
}